import { designService } from '@paragon/data-connections';

import { CancelTrigger, CancelTriggers } from '../../library/CancelTrigger';
import { getActiveAnalysisSet } from '../selectors/getActiveAnalysisSetSelector';
import { getAnalysisResultGuids } from '../selectors/getAnalysisResultsForActiveAnalysisSetSelector';
import {
  clearAnalysisResults,
  fetchAnalysisResultCanceled,
  fetchAnalysisResultFailed,
  fetchAnalysisResultRequested,
  fetchAnalysisResultSucceeded,
} from '../slices/analysisResultRequestsSlice';
import { AsyncThunkActionVoid } from './thunkActions';

// This was chrome's connection limit with HTTP 1.1, so we decided to stick with it.
// https://stackoverflow.com/questions/985431/max-parallel-http-connections-in-a-browser
const CHUNK_SIZE = 6;

const cancelRequests = new CancelTriggers();
export function cancelActiveAnalysisResultRequests() {
  cancelRequests.trigger();
}

const fetchAnalysisResult =
  (analysisResultGuid: string, cancelTrigger: CancelTrigger): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    let analysisResult = null;
    const { openContext } = getState().application;
    const sharedComponentSetLookupCode =
      openContext?.type === 'shared-component-set' ? openContext.sharedComponentSetLookupCode : undefined;
    try {
      analysisResult = await designService.analysisResults.get(
        analysisResultGuid,
        sharedComponentSetLookupCode,
        cancelTrigger.whenCanceled,
      );
    } catch (err) {
      console.log('Error loading analysis result', err);
    }

    if (cancelTrigger.canceled) {
      return;
    }

    if (analysisResult == null) {
      dispatch(fetchAnalysisResultFailed({ analysisResultGuid }));
      return;
    }

    dispatch(fetchAnalysisResultSucceeded(analysisResult));
  };

export const loadActiveAnalysisResults = (): AsyncThunkActionVoid => async (dispatch, getState) => {
  const state = getState();

  const analysisSet = getActiveAnalysisSet(state);
  if (!analysisSet) {
    return;
  }

  const {
    selections: { selectedCheckResultsLoadCaseGuid },
    analysisResultRequests,
  } = state;

  const guidsOfAnalysisResultsThatOughtToBeLoaded: readonly string[] | null =
    selectedCheckResultsLoadCaseGuid == null || analysisSet.analysisResultGuidsByLoadCase == null
      ? getAnalysisResultGuids(analysisSet)
      : [analysisSet.analysisResultGuidsByLoadCase[selectedCheckResultsLoadCaseGuid]];

  const analysisResultGuids = guidsOfAnalysisResultsThatOughtToBeLoaded?.filter((guid) => {
    const request = analysisResultRequests[guid];
    return !request || request.status === 'error';
  });

  if (!analysisResultGuids?.length) return;

  dispatch(fetchAnalysisResultRequested({ analysisResultGuids }));

  const trigger = cancelRequests.createCancelTrigger(() => {
    dispatch(fetchAnalysisResultCanceled({ analysisResultGuids }));
  });

  try {
    for (let i = 0; i < analysisResultGuids.length; i += CHUNK_SIZE) {
      const promises = analysisResultGuids
        .slice(i, i + CHUNK_SIZE)
        .map((guid) => dispatch(fetchAnalysisResult(guid, trigger)));

      await Promise.all(promises);
    }

    dispatch(clearAnalysisResults({ guidsOfAnalysisResultsToKeep: analysisSet.analysisResultGuids ?? [] }));
  } finally {
    trigger.remove();
  }
};
